import React, { useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Divider,
  Chip,
  Stack,
} from '@mui/material';
import { format } from 'date-fns';
import { OrderHardware, OrderProduct, OrderAddon, CouponValidateResponse } from '../../types/order';
import { couponStatus } from '../../utils/coupon';
import BonusNotice from './BonusNotice';

const API_BASE = process.env.REACT_APP_API_URL || (typeof window !== 'undefined' ? window.location.origin : '');

const CONTRACT_TYPE_LABELS: Record<string, string> = {
  no_contract: 'No Contract',
  '12_month': '12 Month Contract',
  '24_month': '24 Month Contract',
};

interface OrderSummaryProps {
  internetPlan: OrderProduct | null;
  equipment: OrderHardware[];
  addOns?: OrderAddon[];
  voicePlan: OrderProduct | null;
  voiceChannels: number;
  voiceServiceSelected: boolean;
  customerData: any;
  customerType: 'residential' | 'business';
  /** Address captured on plan selection step */
  serviceAddressStep1: string;
  /** Requested service activation date (yyyy-MM-dd) captured on plan selection step */
  requestedActivationDate?: string;
  paymentMethod?: 'credit_card' | 'direct_debit' | null;
  categoryName?: string;
  subcategoryName?: string;
  /** Order subcategory slug — shown on review if it matches a Service Types `code` with an uploaded logo */
  serviceTypeCodeForLogo?: string;
  portingNumber?: string;
  hasPortingDocument?: boolean;
  appliedCoupon?: CouponValidateResponse | null;
  contractType?: string;
}

const sectionTitleSx = {
  fontSize: '0.7rem',
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase' as const,
  color: 'primary.main',
  mb: 1.5,
};

const cardSx = {
  p: 2.5,
  borderRadius: 2,
  height: '100%',
  border: '1px solid',
  borderColor: 'divider',
  boxShadow: '0 1px 3px rgba(13, 31, 60, 0.06)',
  background: 'linear-gradient(180deg, #ffffff 0%, #fafbfd 100%)',
};

/** Coerce API/string values — avoids crash when monthly_price etc. are strings (truthy so `|| 0` does not help). */
function formatPrice(price: unknown) {
  const n =
    typeof price === 'number'
      ? price
      : parseFloat(String(price === null || price === undefined ? '' : price));
  const v = Number.isFinite(n) ? n : 0;
  return `$${v.toFixed(2)}`;
}

function formatDob(d: unknown): string | null {
  if (!d) return null;
  try {
    const dt = d instanceof Date ? d : new Date(d as string);
    if (Number.isNaN(dt.getTime())) return null;
    return format(dt, 'dd/MM/yyyy');
  } catch {
    return null;
  }
}

function serviceTypeLogoUrl(code: string): string {
  const base = API_BASE.replace(/\/$/, '');
  return `${base}/api/orders/service-type-logos/${encodeURIComponent(code.trim().toLowerCase())}`;
}

function ServiceTypeLogoMark({ code }: { code: string }) {
  const [visible, setVisible] = useState(true);
  if (!visible || !code.trim()) return null;
  return (
    <Box
      component="img"
      src={serviceTypeLogoUrl(code)}
      alt=""
      sx={{
        maxHeight: 52,
        maxWidth: '100%',
        width: 'auto',
        height: 'auto',
        objectFit: 'contain',
        objectPosition: 'right top',
        display: 'block',
      }}
      onError={() => setVisible(false)}
    />
  );
}

function hardwareImageUrl(item: OrderHardware): string | null {
  if (!item.image_path) return null;
  const base = API_BASE.replace(/\/$/, '');
  return `${base}/api/orders/hardware-images/${item.id}`;
}

function productImageUrl(product: OrderProduct): string | null {
  if (!product.image_path) return null;
  const base = API_BASE.replace(/\/$/, '');
  return `${base}/api/orders/product-images/${product.id}`;
}

function ProductThumb({ product }: { product: OrderProduct }) {
  const [visible, setVisible] = useState(true);
  const url = productImageUrl(product);
  if (!url || !visible) {
    return (
      <Box
        sx={{
          width: 56,
          height: 56,
          flexShrink: 0,
          borderRadius: 1,
          bgcolor: 'action.hover',
          border: '1px solid',
          borderColor: 'divider',
        }}
      />
    );
  }
  return (
    <Box
      component="img"
      src={url}
      alt=""
      sx={{
        width: 56,
        height: 56,
        objectFit: 'contain',
        flexShrink: 0,
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: '#fff',
      }}
      onError={() => setVisible(false)}
    />
  );
}

function HardwareThumb({ item }: { item: OrderHardware }) {
  const [visible, setVisible] = useState(true);
  const url = hardwareImageUrl(item);
  if (!url || !visible) {
    return (
      <Box
        sx={{
          width: 56,
          height: 56,
          flexShrink: 0,
          borderRadius: 1,
          bgcolor: 'action.hover',
          border: '1px solid',
          borderColor: 'divider',
        }}
      />
    );
  }
  return (
    <Box
      component="img"
      src={url}
      alt=""
      sx={{
        width: 56,
        height: 56,
        objectFit: 'contain',
        flexShrink: 0,
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: '#fff',
      }}
      onError={() => setVisible(false)}
    />
  );
}

function OrderSummary({
  internetPlan,
  equipment,
  addOns,
  voicePlan,
  voiceChannels,
  voiceServiceSelected,
  customerData,
  customerType,
  serviceAddressStep1,
  requestedActivationDate,
  paymentMethod,
  categoryName,
  subcategoryName,
  serviceTypeCodeForLogo,
  portingNumber,
  hasPortingDocument,
  appliedCoupon,
  contractType,
}: OrderSummaryProps) {
  const dobLabel = formatDob(customerData?.dateOfBirth);
  const billing = (customerData?.billingAddress || '').trim();
  const serviceAcct = (customerData?.serviceAddress || '').trim();
  const install = (serviceAddressStep1 || '').trim();
  const payHeading =
    paymentMethod === 'credit_card'
      ? 'Credit card'
      : paymentMethod === 'direct_debit'
        ? 'Direct debit (bank account)'
        : 'Payment method';

  return (
    <Box>
      <Typography
        variant="h5"
        component="h2"
        gutterBottom
        sx={{ fontWeight: 700, color: 'primary.dark', letterSpacing: '-0.02em' }}
      >
        Review your order
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 720, lineHeight: 1.65 }}>
        Thank you for choosing Sonic Internet &amp; Voice. Please review your selections below and confirm they match
        what you wish to order. Your payment details were captured securely on the previous step and are not shown again
        here. To complete your application, sign in the box at the bottom of this page.
      </Typography>

      <Grid container spacing={2.5}>
        <Grid item xs={12}>
          <Stack spacing={2.5}>
            <Paper
              elevation={0}
              sx={{
                ...cardSx,
                position: 'relative',
                overflow: 'visible',
              }}
            >
              <Typography
                sx={{
                  ...sectionTitleSx,
                  mb: 1.5,
                  pr: serviceTypeCodeForLogo ? { xs: 14, sm: 20 } : 0,
                  maxWidth: serviceTypeCodeForLogo ? { xs: 'calc(100% - 120px)', sm: 'calc(100% - 176px)' } : undefined,
                }}
              >
                Plan & service location
              </Typography>
              {serviceTypeCodeForLogo ? (
                <Box
                  sx={{
                    position: 'absolute',
                    top: (theme) => theme.spacing(2.5),
                    right: (theme) => theme.spacing(2.5),
                    maxWidth: { xs: 112, sm: 168 },
                    lineHeight: 0,
                  }}
                >
                  <ServiceTypeLogoMark code={serviceTypeCodeForLogo} />
                </Box>
              ) : null}
              <Stack
                spacing={1}
                sx={
                  serviceTypeCodeForLogo
                    ? { maxWidth: { xs: 'calc(100% - 120px)', sm: 'calc(100% - 180px)' } }
                    : undefined
                }
              >
                {(categoryName || subcategoryName) && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.75 }}>
                    {categoryName && <Chip size="small" label={categoryName} variant="outlined" />}
                    {subcategoryName && <Chip size="small" label={subcategoryName} variant="outlined" />}
                  </Box>
                )}
                {internetPlan && (
                  <Typography variant="subtitle1" fontWeight={600}>
                    {internetPlan.name}
                  </Typography>
                )}
                {install && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Installation address
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {install}
                    </Typography>
                  </Box>
                )}
                {requestedActivationDate && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Requested activation date
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {format(new Date(`${requestedActivationDate}T00:00:00`), 'd MMMM yyyy')}
                    </Typography>
                  </Box>
                )}
                {contractType && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Contract length
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {CONTRACT_TYPE_LABELS[contractType] ?? contractType}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </Paper>

            <Paper elevation={0} sx={cardSx}>
              <Typography sx={sectionTitleSx}>Customer & contact</Typography>
              <Stack spacing={1.25}>
                <Typography variant="body1" fontWeight={600}>
                  {customerData?.firstName} {customerData?.lastName}
                </Typography>
                <DetailRow label="Email" value={customerData?.email} />
                <DetailRow label="Mobile" value={customerData?.mobileNumber} />
                {dobLabel && <DetailRow label="Date of birth" value={dobLabel} />}
                {serviceAcct && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Account / correspondence address
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {serviceAcct}
                    </Typography>
                  </Box>
                )}
                {billing && billing !== serviceAcct && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Billing address
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {billing}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </Paper>

            {customerType === 'business' && (
              <Paper elevation={0} sx={cardSx}>
                <Typography sx={sectionTitleSx}>Business</Typography>
                <DetailRow label="Organisation" value={customerData?.organisationName} />
                <DetailRow label="ABN / ACN" value={customerData?.abnAcn} />
              </Paper>
            )}

            {voiceServiceSelected && (
              <Paper elevation={0} sx={cardSx}>
                <Typography sx={sectionTitleSx}>Voice service</Typography>
                {voicePlan ? (
                  <Typography variant="body1" fontWeight={600} gutterBottom>
                    {voicePlan.name}
                  </Typography>
                ) : (
                  <Typography color="text.secondary">Plan selected</Typography>
                )}
                <DetailRow label="Channels" value={String(voiceChannels)} />
                {portingNumber ? <DetailRow label="Porting number" value={portingNumber} /> : null}
                {hasPortingDocument ? (
                  <Typography variant="body2" color="text.secondary">
                    Porting document attached
                  </Typography>
                ) : null}
              </Paper>
            )}

            <Paper elevation={0} sx={cardSx}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                <Typography sx={{ ...sectionTitleSx, mb: 0 }}>Equipment & services</Typography>
                {contractType && (
                  <Typography
                    variant="caption"
                    sx={{
                      px: 1.25,
                      py: 0.4,
                      borderRadius: 1,
                      bgcolor: contractType === 'no_contract' ? 'grey.100' : 'primary.50',
                      color: contractType === 'no_contract' ? 'text.secondary' : 'primary.dark',
                      fontWeight: 600,
                      border: '1px solid',
                      borderColor: contractType === 'no_contract' ? 'divider' : 'primary.light',
                    }}
                  >
                    {CONTRACT_TYPE_LABELS[contractType] ?? contractType}
                  </Typography>
                )}
              </Box>
              <Stack divider={<Divider flexItem />} spacing={1.5}>
                {internetPlan && (
                  <Box
                    sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}
                  >
                    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', minWidth: 0 }}>
                      <ProductThumb product={internetPlan} />
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={600}>
                          {internetPlan.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Internet plan
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      {appliedCoupon && couponStatus(appliedCoupon, contractType || 'no_contract')?.discountActive ? (
                        <>
                          <Typography variant="body2" fontWeight={600} sx={{ whiteSpace: 'nowrap' }}>
                            {formatPrice(Math.max(0, (internetPlan.monthly_price || 0) - Number(appliedCoupon.discount_amount)))}/mo
                            {' · '}setup {formatPrice(internetPlan.setup_price || 0)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                            then {formatPrice(internetPlan.monthly_price || 0)}/mo after {appliedCoupon.discount_months} months
                          </Typography>
                        </>
                      ) : (
                        <Typography variant="body2" fontWeight={600} sx={{ whiteSpace: 'nowrap' }}>
                          {`${formatPrice(internetPlan.monthly_price || 0)}/mo · setup ${formatPrice(
                            internetPlan.setup_price || 0
                          )}`}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                )}
                {(addOns ?? []).map((item) => (
                  <Box
                    key={item.id}
                    sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={600}>
                        {item.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Add-on
                      </Typography>
                    </Box>
                    <Typography variant="body2" fontWeight={600} sx={{ whiteSpace: 'nowrap' }}>
                      {`${formatPrice(item.monthly_price || 0)}/mo`}
                    </Typography>
                  </Box>
                ))}
                {voiceServiceSelected && voicePlan && (
                  <Box
                    sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}
                  >
                    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', minWidth: 0 }}>
                      <ProductThumb product={voicePlan} />
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={600}>
                          {`${voicePlan.name} × ${voiceChannels}`}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Voice
                        </Typography>
                      </Box>
                    </Box>
                    <Typography variant="body2" fontWeight={600} sx={{ whiteSpace: 'nowrap' }}>
                      {`${formatPrice((voicePlan.monthly_price || 0) * voiceChannels)}/mo`}
                    </Typography>
                  </Box>
                )}
                {(equipment ?? []).map((item) => {
                  let price: number;
                  if (contractType === 'no_contract' && item.price_no_contract != null) {
                    price = Number(item.price_no_contract);
                  } else if (contractType === '12_month' && item.price_12_month != null) {
                    price = Number(item.price_12_month);
                  } else if (contractType === '24_month' && item.price_24_month != null) {
                    price = Number(item.price_24_month);
                  } else {
                    price = item.effective_price ?? item.price ?? 0;
                  }
                  return (
                    <Box
                      key={item.id}
                      sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}
                    >
                      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', minWidth: 0 }}>
                        <HardwareThumb item={item} />
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" fontWeight={600}>
                            {item.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Equipment
                          </Typography>
                        </Box>
                      </Box>
                      <Typography variant="body2" fontWeight={600} sx={{ whiteSpace: 'nowrap' }}>
                        {formatPrice(price)}
                      </Typography>
                    </Box>
                  );
                })}
              </Stack>
              {appliedCoupon?.bonus_title && (
                <Box sx={{ mt: 2 }}>
                  <BonusNotice
                    title={appliedCoupon.bonus_title}
                    description={appliedCoupon.bonus_description}
                    terms={appliedCoupon.bonus_terms}
                    needs={couponStatus(appliedCoupon, contractType || 'no_contract')?.bonusNeeds}
                  />
                </Box>
              )}
            </Paper>

            <Paper
              elevation={0}
              sx={{
                ...cardSx,
                borderColor: 'primary.light',
                background: 'linear-gradient(135deg, rgba(13, 31, 60, 0.04) 0%, #ffffff 100%)',
              }}
            >
              <Typography sx={sectionTitleSx}>Payment</Typography>
              <Typography variant="body1" fontWeight={600} gutterBottom>
                {payHeading}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.65 }}>
                Thanks for choosing Sonic Internet &amp; Voice — we appreciate you securely providing your payment
                details.
              </Typography>
            </Paper>
          </Stack>
        </Grid>

      </Grid>
    </Box>
  );
}

function DetailRow({ label, value }: { label: string; value?: string }) {
  if (!value) return null;
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography variant="body2">{value}</Typography>
    </Box>
  );
}

export default OrderSummary;
